/**
 * `PinnedWorkspaceRow` — icon + label + tooltip for a `pinned-workspace` tree
 * row (Task 16). Self-contained: unlike every other row type, it does NOT go
 * through the generic `TreeNodeIcon` lookup (that map is keyed by static
 * `node.type`, but a workspace pin's icon depends on the per-instance
 * `workspaceKind`), so `TreeNodeContent` renders this component in place of
 * the icon-div + name-span pair for `node.type === 'pinned-workspace'`.
 *
 * Tooltip is a plain `title` attribute (matches the `staleTooltip` convention
 * already used in `TreeNodeContent`) rather than the Radix `Tooltip` — the
 * app-wide Radix tooltip primitives require pointer/focus simulation to
 * assert in jsdom, while `title` is directly queryable and behaves as a
 * native OS tooltip on hover.
 */

import { Columns3, FileCode2, Filter, GitCompareArrows, Link2, NotebookText, Table2 } from 'lucide-react';
import type { PinnedWorkspaceNode } from '../../models/nodeTypes';
import type {
  PinnedDataComparePayload,
  PinnedNotebookPayload,
  PinnedQueryPayload,
  PinnedSchemaComparePayload,
  PinnedTableViewPayload,
  PinnedWorkspaceRef,
} from '../../../../types/electron';
import { isFileSourcePin } from '../../../workspace/common/services/workspacePinCreate';

const KIND_ICON: Record<PinnedWorkspaceNode['workspaceKind'], typeof Table2> = {
  tableView: Table2,
  query: FileCode2,
  notebook: NotebookText,
  // Data compare = rows flowing between two tables; schema compare = two
  // structures side by side. Distinct glyphs so the two read apart at a glance.
  dataCompare: GitCompareArrows,
  schemaCompare: Columns3,
};

/** Last path segment — browser-safe, no `node:path` in the renderer. */
function basename(filePath: string): string {
  const parts = filePath.split(/[\\/]/);
  return parts[parts.length - 1] || filePath;
}

function tableViewTooltip(payload: PinnedTableViewPayload): string {
  const filterCount = payload.filters.length;
  const parts = [
    `${payload.schema}.${payload.tableName}`,
    `${filterCount} ${filterCount === 1 ? 'filter' : 'filters'}`,
  ];
  // Only the primary (first) sort is shown — matches the brief's singular
  // "sort: {col} {dir}" template. Omitted entirely when unsorted.
  const primarySort = payload.sorting[0];
  if (primarySort) {
    parts.push(`sort: ${primarySort.field} ${primarySort.direction}`);
  }
  return parts.join(' · ');
}

function queryTooltip(payload: PinnedQueryPayload): string {
  if (payload.source === 'file') {
    return `${basename(payload.filePath)} · ${payload.filePath}`;
  }
  const truncated = payload.query.length > 100 ? `${payload.query.slice(0, 100)}…` : payload.query;
  return truncated;
}

function notebookTooltip(payload: PinnedNotebookPayload): string {
  if (payload.source === 'file') {
    return `${basename(payload.filePath)} · ${payload.filePath}`;
  }
  const n = payload.cells.length;
  return `${n} ${n === 1 ? 'cell' : 'cells'}`;
}

/**
 * `conn / db / schema.table → conn / db / schema.table` is too long for a
 * tooltip row, and the connection aliases aren't available to this pure
 * component anyway. Show the two endpoints the user actually chose.
 */
function dataCompareTooltip(payload: PinnedDataComparePayload): string {
  const side = (e: PinnedDataComparePayload['source']): string => {
    if (!e) return '?';
    if (e.kind === 'blueprint-folder') return `${basename(e.folderPath ?? '')} / ${e.table ?? '?'}`;
    const table = [e.schema, e.table].filter(Boolean).join('.');
    return [e.database, table].filter(Boolean).join(' / ') || '?';
  };
  const mappings = payload.keyMappings?.length
    ? ` · ${payload.keyMappings.length} key${payload.keyMappings.length === 1 ? '' : 's'}`
    : '';
  const included =
    payload.valueMappings?.filter((m: { included: boolean }) => m.included).length ?? 0;
  const values = payload.valueMappings?.length
    ? ` · ${included}/${payload.valueMappings.length} value`
    : '';
  return `${side(payload.source)} → ${side(payload.target)}${mappings}${values}`;
}

function schemaCompareTooltip(payload: PinnedSchemaComparePayload): string {
  const side = (e: PinnedSchemaComparePayload['source']): string =>
    e?.database || (e?.folderPath ? basename(e.folderPath) : '') || '?';
  const scope = payload.compareWholeDatabase === false ? ' · single object' : ' · whole database';
  return `${side(payload.source)} → ${side(payload.target)}${scope}`;
}

function buildPinnedWorkspaceTooltip(ref: PinnedWorkspaceRef): string {
  switch (ref.kind) {
    case 'tableView':
      return tableViewTooltip(ref.payload);
    case 'query':
      return queryTooltip(ref.payload);
    case 'notebook':
      return notebookTooltip(ref.payload);
    case 'dataCompare':
      return dataCompareTooltip(ref.payload);
    case 'schemaCompare':
      return schemaCompareTooltip(ref.payload);
    default:
      // Exhaustive over PinnedWorkspaceRef['kind']; unreachable in practice
      // (mirrors the `default` pattern in `pinDivergence.ts`'s `isPinDiverged`).
      return '';
  }
}

export function PinnedWorkspaceRow({ node }: { node: PinnedWorkspaceNode }) {
  const { pinRef, workspaceKind } = node;
  const Icon = KIND_ICON[workspaceKind];
  const showFilterBadge = pinRef.kind === 'tableView' && pinRef.payload.filters.length > 0;
  const showFileBadge = isFileSourcePin(pinRef);
  const tooltip = buildPinnedWorkspaceTooltip(pinRef);

  return (
    <span className="flex items-center gap-1.5 truncate" title={tooltip}>
      <span className="relative inline-flex shrink-0" data-testid="pinned-workspace-icon" data-workspace-kind={workspaceKind}>
        <Icon className="h-4 w-4" />
        {showFilterBadge && (
          <Filter
            className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 text-muted-foreground"
            data-testid="pin-filter-badge"
            aria-hidden
          />
        )}
      </span>
      <span className="truncate">{node.name}</span>
      {showFileBadge && (
        <Link2
          className="h-3 w-3 shrink-0 text-muted-foreground"
          data-testid="pin-file-badge"
          aria-hidden
        />
      )}
    </span>
  );
}
